import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Box, CircularProgress, Typography } from '@mui/material';
import { isAdmin } from '../utils/adminUtils';

const ProtectedRoute = ({ children, adminOnly = false, ownerOnly = false, profileId = null }) => {
  const { user, loading, userRole } = useAuth();
  const [checkingRole, setCheckingRole] = useState(adminOnly || ownerOnly);
  const [hasAccess, setHasAccess] = useState(false);

  // Antes esto apagaba `checkingRole` en cuanto `user` venia null, sin mirar si
  // era null porque no hay sesion o porque AuthContext todavia no resolvio. En
  // una carga directa por URL siempre es lo segundo, asi que la secuencia era:
  //
  //   loading:true  checkingRole:true   user:null   -> espera
  //   loading:true  checkingRole:FALSE  user:null   -> se apaga el flag
  //   loading:false checkingRole:false  user:OK     -> pasa por el guard de
  //                                                    abajo con hasAccess en
  //                                                    false y NAVEGA
  //   ...medio segundo despues isAdmin devuelve true, ya en otra pagina
  //
  // Medido: el rebote ocurria a los 372ms y la verificacion resolvia a los 870.
  // Por eso entrar a /admin o a /edit-profile/:id pegando la URL rebotaba a
  // /gallery aun siendo admin, mientras que navegando dentro de la app andaba
  // —ahi el usuario ya estaba resuelto y no habia ventana que perder—.
  useEffect(() => {
    // Mientras AuthContext no termine no se sabe nada, y no saber no es lo
    // mismo que no tener permiso.
    if (loading) return;

    if (!user) {
      setHasAccess(false);
      setCheckingRole(false);
      return;
    }

    if (!adminOnly && !ownerOnly) {
      setHasAccess(true);
      setCheckingRole(false);
      return;
    }

    // Se enciende ANTES de la verificacion asincronica: sin esto, el render que
    // ocurre mientras la promesa esta en vuelo pasa por el guard con hasAccess
    // todavia en false.
    setCheckingRole(true);

    let vigente = true;

    (async () => {
      const esAdmin = await isAdmin(user);
      const permitido = adminOnly ? esAdmin : esAdmin || user.id === profileId;

      // Si la ruta cambio mientras se verificaba, este resultado ya no
      // corresponde a lo que se esta mostrando.
      if (!vigente) return;

      setHasAccess(permitido);
      setCheckingRole(false);
    })();

    return () => {
      vigente = false;
    };
  }, [user, loading, userRole, adminOnly, ownerOnly, profileId]);

  if (loading || checkingRole) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="body1" sx={{ mt: 2 }}>
            Verificando acceso...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!user) {
    // Limpiar cualquier dato de autenticación residual en localStorage
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('sb-') || key.includes('supabase') || key.includes('auth')) {
        localStorage.removeItem(key);
      }
    });
    
    return <Navigate to="/login" replace />;
  }

  if ((adminOnly || ownerOnly) && !hasAccess) {
    return <Navigate to="/gallery" />;
  }

  return children;
};

export default ProtectedRoute;
